// Lege pagina na een uitrol (oprichter 1 okt): een tabblad dat al open stond, kent nog de oude
// index.html en vraagt bij een klik in het menu een oud stukje code (/assets/<naam>-<hash>.js) op
// dat na de uitrol niet meer bestaat. De dynamische import faalt dan, en preact-iso `lazy` blijft
// voor altijd "aan het laden": het menu staat er, het midden blijft leeg tot je zelf ververst.
// Hier: één keer vanzelf herladen (met een slot tegen een lus), anders een rustige foutmelding.

/** Hoe lang na een automatische herlaadbeurt we niet nog eens vanzelf herladen. */
export const RELOAD_GUARD_MS = 60_000;
const KEY = 'fb.chunkReload';

export interface ReloadDeps {
  now: () => number;
  /** null als sessionStorage niet bruikbaar is (privévenster, geblokkeerd): dan nooit vanzelf. */
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  reload: () => void;
}

function defaultDeps(): ReloadDeps {
  let storage: ReloadDeps['storage'];
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return { now: () => Date.now(), storage, reload: () => window.location.reload() };
}

/** Al herladen in dit paginaleven (meerdere mislukte imports tegelijk geven één herlaadbeurt). */
let reloading = false;

/**
 * Herlaadt de pagina één keer. Geeft true als er (nu of al eerder in dit paginaleven) herladen
 * wordt; false als dat kort geleden al gebeurde of niet veilig kan: dan toont de aanroeper een
 * foutmelding met een knop.
 */
export function reloadOnce(deps: ReloadDeps = defaultDeps()): boolean {
  if (reloading) return true;
  const { storage } = deps;
  if (!storage) return false;
  try {
    const last = Number(storage.getItem(KEY) ?? 0);
    if (last > 0 && deps.now() - last < RELOAD_GUARD_MS) return false;
    storage.setItem(KEY, String(deps.now()));
  } catch {
    return false;
  }
  reloading = true;
  deps.reload();
  return true;
}

/** Alleen voor tests. */
export function resetReloadState(): void {
  reloading = false;
}

/**
 * Laadt een scherm en vangt een mislukte import op: de belofte faalt nooit (anders blijft de
 * route leeg). Bij een fout één keer herladen (`whileReloading` tot de pagina weg is), en anders
 * `onError` met een herlaadknop.
 */
export function loadOrFallback<T>(
  load: () => Promise<T>,
  fallback: { whileReloading: T; onError: T },
  deps?: ReloadDeps,
): Promise<T> {
  return load().catch((err: unknown) => {
    console.error(err);
    return reloadOnce(deps) ? fallback.whileReloading : fallback.onError;
  });
}
